import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageContext } from "../../Providers/PageContext";
import { getMainCameraId } from "./GetCamId";

// Scss
import "./scanCam.scss";

// Constants for camera detection
const BACK_INDICATORS = ["back", "rear", "environment", "wide", "world"];
const EXCLUDE_INDICATORS = ["front", "selfie", "user", "face"];

const ScanCam = (props: any) => {
  //  =============================  VARIABLES  ===============================

  const { t } = useTranslation(); // Translate
  const { setImg, isIos, setCaptureType } = usePageContext();

  const [camId, setCamId] = useState<string>("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraCapabilities, setCameraCapabilities] =
    useState<MediaTrackCapabilities | null>(null);

  const [manualBtn, setManualBtn] = useState<boolean | null>(null); // To control click btn visibility
  const [autoBtn, setAutoBtn] = useState<boolean | null>(null); // To control click btn visibility

  const [autoCapture, setAutoCapture] = useState<boolean>(false); // To capture automatically

  // ==============================  FUNCTIONS  =================================

  // Initial Call
  useEffect(() => {
    window.scrollTo({ top: 0 });
    checkCamPermission();

    // Cleanup function to stop all tracks when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Advanced camera selection logic adapted from VideoSDK
  const findBestBackCamera = async (): Promise<string> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );

      if (videoDevices.length === 0) {
        return "";
      }

      // First, try to find a back camera by checking labels
      for (const device of videoDevices) {
        const label = device.label.toLowerCase();
        const isBackCamera = BACK_INDICATORS.some((indicator) =>
          label.includes(indicator)
        );
        const isExcluded = EXCLUDE_INDICATORS.some((indicator) =>
          label.includes(indicator)
        );

        if (isBackCamera && !isExcluded) {
          console.log("Selected camera:", device.label);
          return device.deviceId;
        }
      }

      // If no back camera found, use default from getMainCameraId as fallback
      const cameraDetails = await getMainCameraId(isIos, !isIos);
      return (
        (cameraDetails?.deviceId as string) || videoDevices[0]?.deviceId || ""
      );
    } catch (error) {
      console.error("Error finding best back camera:", error);
      return "";
    }
  };

  const checkCamPermission = async () => {
    try {
      // First try to get temporary stream just to check permissions
      const tempStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: true,
      });

      // Get first video track to check capabilities
      const videoTrack = tempStream.getVideoTracks()[0];
      if (videoTrack && typeof videoTrack.getCapabilities === "function") {
        setCameraCapabilities(videoTrack.getCapabilities());
      }

      // Stop all tracks after checking
      tempStream.getTracks().forEach((t) => t.stop());

      // Use advanced camera selection
      const bestCamId = await findBestBackCamera();
      setCamId(bestCamId);
    } catch (err: any) {
      console.log("Camera permission error: " + err.name + ": " + err.message);

      if (err.name === "NotReadableError") {
        // Camera might be in use by another application
        const bestCamId = await findBestBackCamera();
        setCamId(bestCamId);
      } else {
        // Permission denied or other error
        console.log("Camera access denied");
        props.denied();
      }
    }
  };

  useEffect(() => {
    if (camId) {
      playVideo();
    }
  }, [camId]);

  // Enhanced video constraints based on device capabilities
  const getOptimalConstraints = () => {
    // Default high-quality constraints
    const highEndQuality = {
      width: { min: 1920, ideal: 1920, max: 1920 }, // Remove later as noted
      height: { min: 1080, ideal: 1080, max: 1080 }, // Remove later as noted
      frameRate: { min: 10, ideal: 10, max: 10 }, // Remove later as noted
    };

    // If we have camera capabilities, use them to optimize constraints
    // if (cameraCapabilities) {
    //   return {
    //     width: cameraCapabilities.width
    //       ? { ideal: Math.min(cameraCapabilities.width.max || 3840, 3840) }
    //       : highEndQuality.width,
    //     height: cameraCapabilities.height
    //       ? { ideal: Math.min(cameraCapabilities.height.max || 2160, 2160) }
    //       : highEndQuality.height,
    //     frameRate: cameraCapabilities.frameRate
    //       ? { ideal: Math.min(cameraCapabilities.frameRate.max || 30, 30) }
    //       : highEndQuality.frameRate
    //   };
    // }

    return highEndQuality;
  };

  // play video with enhanced constraints
  const playVideo = () => {
    // Get optimal constraints based on device capabilities
    const optimalConstraints = getOptimalConstraints();

    // Device-specific constraints with optimal settings
    const constraints = isIos
      ? {
          audio: false,
          video: {
            deviceId:
              camId && camId !== "unknown" ? { exact: camId } : undefined,
            ...optimalConstraints,
            facingMode: { exact: "environment" },
            // Removed aspectRatio constraint to allow full resolution
          },
        }
      : {
          audio: false,
          video: {
            deviceId:
              camId && camId !== "unknown" ? { exact: camId } : undefined,
            ...optimalConstraints,
            facingMode: { exact: "environment" },
            // Removed aspectRatio constraint to allow full resolution
          },
        };

    console.log("Using video constraints:", constraints);
    window.scrollTo({ top: 0 });

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream: MediaStream) => {
        setStream(stream);

        // Log the actual settings of the selected camera
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          console.log("Camera stream settings:", settings);
        }
      })
      .catch((err) => {
        console.log("Camera access error: " + err.name + ": " + err.message);

        // Try again with simpler constraints if detailed ones fail
        const fallbackConstraints = {
          audio: false,
          video: {
            deviceId:
              camId && camId !== "unknown" ? { exact: camId } : undefined,
            facingMode: { exact: "environment" },
          },
        };

        navigator.mediaDevices
          .getUserMedia(fallbackConstraints)
          .then((fallbackStream) => {
            console.log("Using fallback constraints");
            setStream(fallbackStream);
          })
          .catch((fallbackErr) => {
            console.log("Fallback camera error:", fallbackErr);
            props.denied();
          });
      });
  };

  // video Stream
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          videoRef.current.play();
          setManualBtn(true);
          setAutoBtn(true);
        }
      };
    }
  }, [stream]);

  // Handle btn click
  const handleCapture = (captureType: string) => {
    setCaptureType(captureType);
    autoBtn && setAutoBtn(false);
    manualBtn && setManualBtn(false);
    if (captureType === "auto") {
      setAutoCapture(true);
      setManualBtn(null);
    } else {
      setManualBtn(false);
    }
  };

  // For manual Capture
  useEffect(() => {
    if (manualBtn !== null && manualBtn === false) {
      captureImg();
    }
  }, [manualBtn]);

  // For auto Capture
  useEffect(() => {
    autoCapture && captureImg();
  }, [autoCapture]);

  // Enhanced image capture with maximum quality
  const captureImg = () => {
    if (!videoRef.current) return;

    // Create canvas with video dimensions
    const canvas = document.createElement("canvas");
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;

    // Set canvas to full video resolution
    canvas.width = videoWidth;
    canvas.height = videoHeight;

    // Draw the video frame to canvas
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // Apply any image processing improvements here if needed
      ctx.drawImage(videoRef.current, 0, 0, videoWidth, videoHeight);

      // Get image data with maximum quality (1.0)
      const imageDataUrl = canvas.toDataURL("image/jpeg", 1.0);

      // Log image dimensions and estimated size
      const estimatedSize = Math.round((imageDataUrl.length * 3) / 4 / 1024);
      console.log(
        `Captured image: ${videoWidth}x${videoHeight}, ~${estimatedSize}KB`
      );

      setImg(imageDataUrl);
    }
  };

  // Next Capture
  useEffect(() => {
    const match = /uploaded/;
    if (match.test(props.endText)) {
      if (autoCapture) {
        if (isIos) {
          setTimeout(() => {
            captureImg();
          }, 1000);
        } else {
          captureImg();
        }
      } else {
        setManualBtn(true);
      }
    } else if (props.endText === "Done") {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  }, [props.endText]);

  // ===============================  HTML  ================================

  // Side angles
  const angle = () => {
    return (
      <>
        <div className="angle angle-1"></div>
        <div className="angle angle-2"></div>
        <div className="angle angle-3"></div>
        <div className="angle angle-4"></div>
      </>
    );
  };

  // Overlay
  const overlay = () => {
    return (
      <img
        className="position-absolute start-50 top-50 translate-middle overlay"
        style={{ width: "22%" }}
        src={props.overlay}
        alt=""
      />
    );
  };

  // camera text
  const openingCam = () => {
    return (
      <h3 className="w-100 position-absolute top-50 start-50 translate-middle opening-cam-txt">
        <i>{t("openingCam")}</i>
      </h3>
    );
  };

  // Scan Button
  const scanBtn = () => {
    return (
      <div 
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          width: "100%",
          gap: "20px"
        }}
      >
        {manualBtn && (
          <button className="click-btn" onClick={() => handleCapture("manual")}>
            {t("scan")}
          </button>
        )}
        {autoBtn && (
          <button
            className="click-btn btn-inverse"
            onClick={() => handleCapture("auto")}
          >
            {t("autoScan")}
          </button>
        )}
      </div>
    );
  };

  const subComps = () => {
    return (
      <>
        {angle()}
        {props.productName === "RLC975" && props.overlay && overlay()}
        <div 
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            textAlign: "center",
            width: "80%"
          }}
        >
          <p 
            className="end-p-text" 
            style={{
              margin: 0,
              padding: "10px",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              borderRadius: "5px"
            }}
          >
            {props.endText}
          </p>
          {autoCapture && 
            <p 
              className="end-p-text" 
              style={{
                margin: "5px 0 0 0",
                padding: "5px 10px",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                borderRadius: "5px"
              }}
            >
              {t("autoEnabled")}
            </p>
          }
        </div>
      </>
    );
  };

  // Main
  return (
    <section className="position-relative" style={{ height: "100vh" }}>
      <div className="cam-div">
        <video id="vid" ref={videoRef} autoPlay={true} muted playsInline />
        {stream ? subComps() : openingCam()}
      </div>
      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: "10%",
          width: "100%",
          textAlign: "center",
          transform: "translate(-50%, 0)",
        }}
      >
        {scanBtn()}
      </div>
    </section>
  );
};

export default ScanCam;